import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { fetchJson } from '../api';
import ListingList from '../components/ListingList';
import { generateWebsiteSchema, generateOrganizationSchema, generateBreadcrumbSchema, generateSchemaGraph } from '../utils/schema';
import { generateCompleteSEO, generateLocationKeywords } from '../utils/seo';
import SEOHead, { SEOLinks } from '../components/SEOHead';
import { getSiteBaseUrl } from '../config/site';
import { buildBusinessUrl } from '../utils/directoryUrls';

const CategorySchema = ({ stateSlug, citySlug, categorySlug, categoryName, cityName, stateName, listings, total }) => {
  const baseUrl = getSiteBaseUrl();
  const pageUrl = `${baseUrl}/in/${stateSlug}/${citySlug}/${categorySlug}`;
  
  const schema = generateSchemaGraph([
    generateWebsiteSchema(),
    generateOrganizationSchema(),
    generateBreadcrumbSchema([
      { name: 'Home', url: baseUrl },
      { name: stateName, url: `${baseUrl}/in/${stateSlug}` },
      { name: cityName, url: `${baseUrl}/in/${stateSlug}/${citySlug}` },
      { name: categoryName, url: pageUrl }
    ]),
    {
      '@type': 'CollectionPage',
      '@id': `${pageUrl}/#webpage`,
      url: pageUrl,
      name: `Best ${categoryName} in ${cityName}, ${stateName} | Visibeen Directory`,
      description: `Top ${total || 0} ${categoryName} services in ${cityName}. Compare ratings, reviews and get contact details.`,
      isPartOf: { '@id': `${baseUrl}/#website` },
      about: {
        '@type': 'Service',
        name: categoryName,
        areaServed: {
          '@type': 'City',
          name: cityName
        }
      },
      mainEntity: {
        '@type': 'ItemList',
        name: `${categoryName} in ${cityName}`,
        numberOfItems: total || listings?.length || 0,
        itemListElement: (listings || []).slice(0, 10).map((biz, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          item: {
            '@type': 'LocalBusiness',
            name: biz.name,
            url: buildBusinessUrl({
              stateSlug,
              citySlug,
              categorySlug: biz.category_slug || biz.categorySlug || categorySlug,
              slug: biz.slug
            }, baseUrl),
            image: biz.logo || biz.image || '',
            telephone: biz.phone || '',
            address: {
              '@type': 'PostalAddress',
              streetAddress: biz.address || '',
              addressLocality: cityName,
              addressRegion: stateName
            },
            ...(biz.rating && {
              aggregateRating: {
                '@type': 'AggregateRating',
                ratingValue: biz.rating,
                reviewCount: biz.review_count || 0
              }
            })
          }
        }))
      }
    }
  ]);

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
};

export default function CategoryPage() {
  const { stateSlug, citySlug, categorySlug } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const page = parseInt(searchParams.get('page') || '1', 10);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const res = await fetchJson(`/api/v1/directory/in/${stateSlug}/${citySlug}/${categorySlug}/listings?page=${page}`);
        setData(res);
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [stateSlug, citySlug, categorySlug, page]);

  if (loading) return <div>Loading listings…</div>;
  if (error) return <div className="error">{error}</div>;

  const nextPage = () => setSearchParams({ page: String(page + 1) });
  const prevPage = () => setSearchParams({ page: String(Math.max(1, page - 1)) });
  const totalPages = Math.ceil((data?.total || 0) / (data?.pageSize || 20));

  const categoryName = data?.category?.name || categorySlug;
  const cityName = data?.city?.name || citySlug;
  const stateName = data?.state?.name || stateSlug;
  const listings = data?.listings || [];

  const formattedCategoryName = categoryName.charAt(0).toUpperCase() + categoryName.slice(1).replace(/-/g, ' ');
  const formattedCityName = cityName.charAt(0).toUpperCase() + cityName.slice(1).replace(/-/g, ' ');
  const formattedStateName = stateName.charAt(0).toUpperCase() + stateName.slice(1).replace(/-/g, ' ');

  const seoData = generateCompleteSEO({
    title: `Best ${formattedCategoryName} in ${formattedCityName}, ${formattedStateName}`,
    description: `Top ${data?.total || 0} ${formattedCategoryName} services in ${formattedCityName}. Compare ratings, reviews and get contact details. Find the best ${formattedCategoryName} businesses near you.`,
    keywords: generateLocationKeywords(formattedCityName, formattedStateName, formattedCategoryName),
    url: `/in/${stateSlug}/${citySlug}/${categorySlug}`,
    type: 'website'
  });

  const pageTitle = page > 1 ? `${seoData.title} - Page ${page}` : seoData.title;
  // Canonical best-practice for pagination:
  // - Canonical should point to the main (page=1) URL.
  // - Page 2+ should be crawlable via rel=prev/next but not canonicalized to themselves.
  // - We also keep `noindex,follow` on page 2+.
  const seoBaseUrl = getSiteBaseUrl();
  const canonicalUrl = `${seoBaseUrl}/in/${stateSlug}/${citySlug}/${categorySlug}`;

  const prevUrl = page > 1
    ? `${seoBaseUrl}/in/${stateSlug}/${citySlug}/${categorySlug}${page > 2 ? `?page=${page - 1}` : ''}`
    : null;

  const nextUrl = page < totalPages
    ? `${seoBaseUrl}/in/${stateSlug}/${citySlug}/${categorySlug}?page=${page + 1}`
    : null;

  // OG URL should match the *current* page URL (including pagination parameter)
  const ogUrl = page > 1
    ? `${seoBaseUrl}/in/${stateSlug}/${citySlug}/${categorySlug}?page=${page}`
    : `${seoBaseUrl}/in/${stateSlug}/${citySlug}/${categorySlug}`;

  return (
    <>
      <SEOHead
        title={pageTitle}
        description={seoData.meta.description}
        keywords={seoData.meta.keywords}
        canonical={canonicalUrl}
        robots={page > 1 ? 'noindex, follow' : 'index, follow'}
        ogTitle={pageTitle}
        ogDescription={seoData.meta['og:description']}
        ogUrl={ogUrl}
        ogType="website"
        twitterTitle={pageTitle}
        twitterDescription={seoData.meta['twitter:description']}
        geoRegion={`IN-${stateSlug.toUpperCase()}`}
        geoPlacename={formattedCityName}
      />

      <SEOLinks prev={prevUrl} next={nextUrl} />

      <CategorySchema
        stateSlug={stateSlug}
        citySlug={citySlug}
        categorySlug={categorySlug}
        categoryName={categoryName}
        cityName={cityName}
        stateName={stateName}
        listings={listings}
        total={data?.total}
      />
      <ListingList
        listings={listings}
        categoryName={categoryName}
        cityName={cityName}
        stateName={stateName}
        stateSlug={stateSlug}
        citySlug={citySlug}
        categorySlug={categorySlug}
        page={page}
        totalPages={totalPages}
        onNextPage={nextPage}
        onPrevPage={prevPage}
      />
    </>
  );
}

